import { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Tag,
  Table,
  Spin,
  Empty,
  Timeline,
  Progress,
  Badge,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CloudUploadOutlined,
  CarOutlined,
  TeamOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ExperimentOutlined,
  FieldTimeOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useDateRange } from "../layout/AppLayout";
import axios from "../../services/axiosInstance";
import {
  CP_HIGHLIGHTS,
  CP_UPLOAD_TREND,
  CP_DOC_TYPE_BREAKDOWN,
  CP_VEHICLE_COMPLETION,
  CP_WEIGHT_ANALYTICS,
  CP_RAKE_ANALYTICS,
  CP_HOURLY_DISTRIBUTION,
  CP_TOP_UPLOADERS,
  CP_DOCUMENT_EXPIRY,
  CP_OPERATIONS_TREND,
  CP_RECENT_ACTIVITY,
  CP_ENTITY_COUNTS,
} from "../../services/apiEndPoints";
import type {
  DashboardHighlights,
  UploadTrend,
  DocTypeBreakdown,
  VehicleCompletion,
  WeightAnalytics,
  RakeAnalytics,
  HourlyDistribution,
  TopUploaders,
  DocumentExpiry,
  OperationsTrend,
  RecentActivity,
  EntityCounts,
} from "../types/dashboard.types";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

/* ─── Constants ─────────────────────────────────────────── */

const COLORS = [
  "#1677ff",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#ef4444",
  "#14b8a6",
  "#a855f7",
  "#f97316",
];

const formatNumber = (n: number) => n.toLocaleString("en-IN");

const formatWeight = (kg: number) => {
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toFixed(2)}M kg`;
  if (kg >= 1_000) return `${(kg / 1_000).toFixed(1)}K kg`;
  return `${kg} kg`;
};

const formatDocType = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const getDateParams = (dateRange: [dayjs.Dayjs, dayjs.Dayjs]) => ({
  fromDate: dateRange[0].format("YYYY-MM-DD"),
  toDate: dateRange[1].format("YYYY-MM-DD"),
});

/* ─── Stat Card ─────────────────────────────────────────── */

const StatCard = ({
  label,
  value,
  change,
  icon,
  color,
  gradient,
  suffix,
}: {
  label: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
  color: string;
  gradient: string;
  suffix?: string;
}) => (
  <Card
    className="stat-card"
    style={{ borderRadius: 14, border: "1px solid #e2e8f0" }}
    styles={{ body: { padding: "20px 22px" } }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <Text
          style={{
            fontSize: 13,
            color: "#64748b",
            display: "block",
            marginBottom: 6,
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1.1,
          }}
        >
          {value}
        </Text>
        {suffix && (
          <Text
            style={{
              fontSize: 12,
              color: "#94a3b8",
              display: "block",
              marginTop: 2,
            }}
          >
            {suffix}
          </Text>
        )}
        {change !== undefined && (
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Tag
              color={change >= 0 ? "success" : "error"}
              style={{
                margin: 0,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
              {Math.abs(change)}%
            </Tag>
            <Text style={{ color: "#94a3b8", fontSize: 11 }}>
              vs prev period
            </Text>
          </div>
        )}
      </div>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          background: gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          color: "#fff",
          flexShrink: 0,
          boxShadow: `0 4px 12px ${color}30`,
        }}
      >
        {icon}
      </div>
    </div>
  </Card>
);

/* ─── Section Card ──────────────────────────────────────── */

const SectionCard = ({
  title,
  subtitle,
  children,
  style,
  extra,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  extra?: React.ReactNode;
}) => (
  <Card
    className="content-card"
    style={{ height: "100%", ...style }}
    styles={{ body: { padding: "20px 24px" } }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 16,
      }}
    >
      <div>
        <Text strong style={{ fontSize: 16 }}>
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              color: "#94a3b8",
              fontSize: 12,
              display: "block",
              marginTop: 2,
            }}
          >
            {subtitle}
          </Text>
        )}
      </div>
      {extra}
    </div>
    {children}
  </Card>
);

/* ─── Component ─────────────────────────────────────────── */

const ControlPlaneDashboard = () => {
  const { dateRange } = useDateRange();

  /* ── state ── */
  const [loading, setLoading] = useState(true);
  const [highlights, setHighlights] = useState<DashboardHighlights | null>(
    null
  );
  const [uploadTrend, setUploadTrend] = useState<UploadTrend | null>(null);
  const [docBreakdown, setDocBreakdown] = useState<DocTypeBreakdown | null>(
    null
  );
  const [vehicleComp, setVehicleComp] = useState<VehicleCompletion | null>(
    null
  );
  const [weightData, setWeightData] = useState<WeightAnalytics | null>(null);
  const [rakeData, setRakeData] = useState<RakeAnalytics | null>(null);
  const [hourlyData, setHourlyData] = useState<HourlyDistribution | null>(
    null
  );
  const [uploaders, setUploaders] = useState<TopUploaders | null>(null);
  const [docExpiry, setDocExpiry] = useState<DocumentExpiry | null>(null);
  const [opsTrend, setOpsTrend] = useState<OperationsTrend | null>(null);
  const [activity, setActivity] = useState<RecentActivity | null>(null);
  const [entityCounts, setEntityCounts] = useState<EntityCounts | null>(null);

  /* ── fetch ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = getDateParams(dateRange);
    try {
      const [
        hlRes,
        trendRes,
        docRes,
        vehRes,
        weightRes,
        rakeRes,
        hourlyRes,
        uploadersRes,
        expiryRes,
        opsRes,
        actRes,
        entityRes,
      ] = await Promise.allSettled([
        axios.get(CP_HIGHLIGHTS, { params }),
        axios.get(CP_UPLOAD_TREND, { params }),
        axios.get(CP_DOC_TYPE_BREAKDOWN, { params }),
        axios.get(CP_VEHICLE_COMPLETION, { params }),
        axios.get(CP_WEIGHT_ANALYTICS, { params }),
        axios.get(CP_RAKE_ANALYTICS, { params }),
        axios.get(CP_HOURLY_DISTRIBUTION, { params }),
        axios.get(CP_TOP_UPLOADERS, { params }),
        axios.get(CP_DOCUMENT_EXPIRY),
        axios.get(CP_OPERATIONS_TREND, { params }),
        axios.get(`${CP_RECENT_ACTIVITY}?limit=30`),
        axios.get(CP_ENTITY_COUNTS, { params }),
      ]);

      // API responses may be wrapped as { data: { ... } } or returned flat
      const unwrap = (res: any) => res?.data ?? res;

      if (hlRes.status === "fulfilled") setHighlights(unwrap(hlRes.value.data));
      if (trendRes.status === "fulfilled") setUploadTrend(unwrap(trendRes.value.data));
      if (docRes.status === "fulfilled") setDocBreakdown(unwrap(docRes.value.data));
      if (vehRes.status === "fulfilled") setVehicleComp(unwrap(vehRes.value.data));
      if (weightRes.status === "fulfilled") setWeightData(unwrap(weightRes.value.data));
      if (rakeRes.status === "fulfilled") setRakeData(unwrap(rakeRes.value.data));
      if (hourlyRes.status === "fulfilled")
        setHourlyData(unwrap(hourlyRes.value.data));
      if (uploadersRes.status === "fulfilled")
        setUploaders(unwrap(uploadersRes.value.data));
      if (expiryRes.status === "fulfilled") setDocExpiry(unwrap(expiryRes.value.data));
      if (opsRes.status === "fulfilled") setOpsTrend(unwrap(opsRes.value.data));
      if (actRes.status === "fulfilled") setActivity(unwrap(actRes.value.data));
      if (entityRes.status === "fulfilled")
        setEntityCounts(unwrap(entityRes.value.data));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ── greeting ── */
  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? "Good morning"
      : now.getHours() < 17
      ? "Good afternoon"
      : "Good evening";
  const dateStr = now.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  /* ── derived data ── */
  const trendChartData =
    uploadTrend?.current?.map((c, i) => ({
      date: dayjs(c.date).format("DD MMM"),
      current: c.count,
      previous: uploadTrend.previous?.[i]?.count ?? 0,
    })) ?? [];

  const expiryChartData = docExpiry?.summary
    ? [
        { label: "Expired", value: docExpiry.summary.expired ?? 0, color: "#ef4444" },
        {
          label: "30 Days",
          value: docExpiry.summary.next30Days ?? 0,
          color: "#f59e0b",
        },
        {
          label: "60 Days",
          value: docExpiry.summary.next60Days ?? 0,
          color: "#f97316",
        },
        {
          label: "90 Days",
          value: docExpiry.summary.next90Days ?? 0,
          color: "#06b6d4",
        },
        { label: "Safe", value: docExpiry.summary.safe ?? 0, color: "#10b981" },
      ]
    : [];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* ─── Welcome Header ─────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          borderRadius: 16,
          padding: "28px 32px",
          marginBottom: 24,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -20,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(22,119,255,0.08)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -30,
            right: 100,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(139,92,246,0.06)",
          }}
        />
        <Row justify="space-between" align="middle">
          <Col>
            <Title
              level={3}
              style={{ color: "#fff", margin: 0, fontWeight: 700 }}
            >
              {greeting}!
            </Title>
            <Text
              style={{
                color: "#94a3b8",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 4,
              }}
            >
              <CalendarOutlined /> {dateStr}
            </Text>
          </Col>
          <Col>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <div style={{ textAlign: "right" }}>
                <Text
                  style={{
                    color: "#94a3b8",
                    fontSize: 12,
                    display: "block",
                  }}
                >
                  Control Plane
                </Text>
                <Badge
                  status="success"
                  text={
                    <Text
                      style={{
                        color: "#10b981",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      All systems operational
                    </Text>
                  }
                />
              </div>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "rgba(16,185,129,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ThunderboltOutlined
                  style={{ color: "#10b981", fontSize: 20 }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── KPI Cards ──────────────────────────────────── */}
      {highlights && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Total Uploads"
                value={formatNumber(highlights.totalWorkflowUploads)}
                change={highlights.uploadsChange}
                icon={<CloudUploadOutlined />}
                color="#1677ff"
                gradient="linear-gradient(135deg, #1677ff, #4096ff)"
                suffix={`${highlights.todayWorkflowUploads} today · ${highlights.avgUploadsPerDay}/day avg`}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Vehicles"
                value={formatNumber(highlights.totalVehicles)}
                icon={<CarOutlined />}
                color="#10b981"
                gradient="linear-gradient(135deg, #10b981, #34d399)"
                suffix={`${highlights.todayVehicles} active today`}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Rakes"
                value={formatNumber(highlights.totalRakes)}
                change={highlights.rakesChange}
                icon={<DatabaseOutlined />}
                color="#8b5cf6"
                gradient="linear-gradient(135deg, #8b5cf6, #a78bfa)"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Documents"
                value={formatNumber(highlights.totalDocuments)}
                icon={<FileTextOutlined />}
                color="#f59e0b"
                gradient="linear-gradient(135deg, #f59e0b, #fbbf24)"
                suffix={`${highlights.expiringDocuments} expiring soon`}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Drivers"
                value={formatNumber(highlights.totalDrivers)}
                icon={<TeamOutlined />}
                color="#06b6d4"
                gradient="linear-gradient(135deg, #06b6d4, #22d3ee)"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Labours"
                value={formatNumber(highlights.totalLabours)}
                icon={<UserOutlined />}
                color="#ec4899"
                gradient="linear-gradient(135deg, #ec4899, #f472b6)"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Weightment Slips"
                value={formatNumber(highlights.totalWeightmentSlips)}
                icon={<ExperimentOutlined />}
                color="#14b8a6"
                gradient="linear-gradient(135deg, #14b8a6, #2dd4bf)"
                suffix={`${highlights.totalChallanSlips} challans · ${highlights.totalTruckSheets} truck sheets`}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                label="Members"
                value={formatNumber(highlights.totalMembers)}
                icon={<SafetyCertificateOutlined />}
                color="#a855f7"
                gradient="linear-gradient(135deg, #a855f7, #c084fc)"
                suffix={`${highlights.totalForm515s} Form 515s`}
              />
            </Col>
          </Row>
        </>
      )}

      {/* ─── Upload Trend + Doc Type Breakdown ──────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={16}>
          <SectionCard
            title="Upload Trend"
            subtitle="Current vs previous period"
            extra={
              <Tag
                style={{
                  margin: 0,
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 12,
                }}
                color="default"
              >
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dateRange[0].format("DD MMM")} —{" "}
                {dateRange[1].format("DD MMM YYYY")}
              </Tag>
            }
          >
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={310}>
                <AreaChart data={trendChartData}>
                  <defs>
                    <linearGradient
                      id="currentGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#1677ff"
                        stopOpacity={0.12}
                      />
                      <stop
                        offset="95%"
                        stopColor="#1677ff"
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="prevGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#94a3b8"
                        stopOpacity={0.12}
                      />
                      <stop
                        offset="95%"
                        stopColor="#94a3b8"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="current"
                    stroke="#1677ff"
                    strokeWidth={2.5}
                    fill="url(#currentGrad)"
                    name="Current Period"
                  />
                  <Area
                    type="monotone"
                    dataKey="previous"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    fill="url(#prevGrad)"
                    name="Previous Period"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No upload trend data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={8}>
          <SectionCard title="Document Types" subtitle="Upload distribution">
            {docBreakdown && docBreakdown.results?.length > 0 ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ResponsiveContainer width="100%" height={210}>
                    <PieChart>
                      <Pie
                        data={docBreakdown.results ?? []}
                        dataKey="count"
                        nameKey="docType"
                        cx="50%"
                        cy="50%"
                        outerRadius={85}
                        innerRadius={55}
                        paddingAngle={3}
                        strokeWidth={0}
                      >
                        {(docBreakdown.results ?? []).map((_: unknown, i: number) => (
                          <Cell
                            key={i}
                            fill={COLORS[i % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 10,
                          fontSize: 13,
                        }}
                        formatter={(value: any, name: any) => [
                          value,
                          formatDocType(String(name)),
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {(docBreakdown.results ?? []).slice(0, 5).map((item, i) => (
                    <div
                      key={item.docType}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            background: COLORS[i % COLORS.length],
                          }}
                        />
                        <Text style={{ fontSize: 12 }}>
                          {formatDocType(item.docType)}
                        </Text>
                      </div>
                      <Text strong style={{ fontSize: 12 }}>
                        {item.count} ({item.percentage}%)
                      </Text>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description="No data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Operations Trend ───────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <SectionCard
            title="Operations Trend"
            subtitle="Daily operations across all modules"
          >
            {opsTrend && opsTrend.results?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={(opsTrend.results ?? []).map((r) => ({
                    ...r,
                    date: dayjs(r.date).format("DD MMM"),
                  }))}
                >
                  <defs>
                    <linearGradient
                      id="wfGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#1677ff"
                        stopOpacity={0.1}
                      />
                      <stop
                        offset="95%"
                        stopColor="#1677ff"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="workflows"
                    stroke="#1677ff"
                    strokeWidth={2}
                    fill="url(#wfGrad)"
                    name="Workflows"
                  />
                  <Area
                    type="monotone"
                    dataKey="rakes"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    fill="none"
                    name="Rakes"
                  />
                  <Area
                    type="monotone"
                    dataKey="weightmentSlips"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="none"
                    name="Weightment"
                  />
                  <Area
                    type="monotone"
                    dataKey="challanSlips"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    fill="none"
                    name="Challans"
                  />
                  <Area
                    type="monotone"
                    dataKey="truckSheets"
                    stroke="#ec4899"
                    strokeWidth={2}
                    fill="none"
                    name="Truck Sheets"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No operations trend data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Weight Analytics + Rake Analytics ──────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14}>
          <SectionCard
            title="Weight Analytics"
            subtitle="Daily gross / tare / net weight trends"
            extra={
              weightData && (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    Gross: {formatWeight(weightData.totalGrossWeight)}
                  </Tag>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    Net: {formatWeight(weightData.totalNetWeight)}
                  </Tag>
                </div>
              )
            }
          >
            {weightData && weightData.dailyTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={(weightData.dailyTrend ?? []).map((d) => ({
                    ...d,
                    date: dayjs(d.date).format("DD MMM"),
                    grossWeight: Math.round(d.grossWeight / 1000),
                    tareWeight: Math.round(d.tareWeight / 1000),
                    netWeight: Math.round(d.netWeight / 1000),
                  }))}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}K`}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                    }}
                    formatter={(v: any) => [`${v}K kg`]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="grossWeight"
                    stroke="#1677ff"
                    strokeWidth={2}
                    dot={false}
                    name="Gross (K kg)"
                  />
                  <Line
                    type="monotone"
                    dataKey="tareWeight"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    name="Tare (K kg)"
                  />
                  <Line
                    type="monotone"
                    dataKey="netWeight"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={false}
                    name="Net (K kg)"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No weight data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={10}>
          <SectionCard
            title="Weight by Material"
            subtitle="Net weight distribution"
          >
            {weightData && weightData.byMaterial?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(weightData.byMaterial ?? []).slice(0, 8).map((m) => ({
                    ...m,
                    name: m.material,
                    netWeight: Math.round(m.netWeight / 1000),
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}K`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                    formatter={(v: any) => [`${v}K kg`]}
                  />
                  <Bar
                    dataKey="netWeight"
                    radius={[0, 6, 6, 0]}
                    name="Net Weight"
                  >
                    {(weightData.byMaterial ?? []).slice(0, 8).map((_, i) => (
                      <Cell
                        key={i}
                        fill={COLORS[i % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No material data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Rake Analytics ─────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={10}>
          <SectionCard
            title="Rake Analytics"
            subtitle="Rakes overview"
            extra={
              rakeData && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    {rakeData.totalRakes} rakes
                  </Tag>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    {rakeData.totalWagons} wagons
                  </Tag>
                </div>
              )
            }
          >
            {rakeData && rakeData.byCompany?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(rakeData.byCompany ?? []).slice(0, 8)}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="company"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="rakeCount"
                    fill="#8b5cf6"
                    radius={[6, 6, 0, 0]}
                    name="Rakes"
                  />
                  <Bar
                    dataKey="totalWagons"
                    fill="#06b6d4"
                    radius={[6, 6, 0, 0]}
                    name="Wagons"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No rake data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={6}>
          <SectionCard
            title="Rake by Variety"
            subtitle="Weight distribution"
          >
            {rakeData && rakeData.byVariety?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={rakeData.byVariety}
                      dataKey="totalWeight"
                      nameKey="variety"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={50}
                      paddingAngle={3}
                      strokeWidth={0}
                    >
                      {(rakeData.byVariety ?? []).map((_, i) => (
                        <Cell
                          key={i}
                          fill={COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 10,
                        fontSize: 13,
                      }}
                      formatter={(v: any) => [formatWeight(Number(v))]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {(rakeData.byVariety ?? []).slice(0, 5).map((v, i) => (
                    <div
                      key={v.variety}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            background: COLORS[i % COLORS.length],
                          }}
                        />
                        <Text style={{ fontSize: 12 }}>{v.variety}</Text>
                      </div>
                      <Text strong style={{ fontSize: 12 }}>
                        {v.count}
                      </Text>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description="No variety data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={8}>
          <SectionCard
            title="Vehicle Completion"
            subtitle={
              vehicleComp
                ? `${vehicleComp.completedVehicles} of ${vehicleComp.totalVehicles} vehicles complete`
                : "Vehicle document progress"
            }
          >
            {vehicleComp ? (
              <>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 16,
                  }}
                >
                  <Progress
                    type="dashboard"
                    percent={vehicleComp.avgCompletion}
                    strokeColor={{
                      "0%": "#1677ff",
                      "100%": "#10b981",
                    }}
                    size={120}
                    format={(p) => (
                      <div>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: "#0f172a",
                          }}
                        >
                          {p}%
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#94a3b8",
                          }}
                        >
                          Avg Completion
                        </div>
                      </div>
                    )}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <Text
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#10b981",
                        display: "block",
                      }}
                    >
                      {vehicleComp.completedVehicles}
                    </Text>
                    <Text style={{ fontSize: 11, color: "#94a3b8" }}>
                      Completed
                    </Text>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <Text
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#f59e0b",
                        display: "block",
                      }}
                    >
                      {vehicleComp.inProgressVehicles}
                    </Text>
                    <Text style={{ fontSize: 11, color: "#94a3b8" }}>
                      In Progress
                    </Text>
                  </div>
                </div>
                {(vehicleComp.vehicles?.length ?? 0) > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      maxHeight: 140,
                      overflowY: "auto",
                    }}
                  >
                    {(vehicleComp.vehicles ?? []).slice(0, 5).map((v) => (
                      <div
                        key={v.vehicleNumber}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 10px",
                          background: "#f8fafc",
                          borderRadius: 8,
                        }}
                      >
                        <Text style={{ fontSize: 12, fontWeight: 500 }}>
                          {v.vehicleNumber}
                        </Text>
                        <Progress
                          percent={v.completionPercent}
                          size="small"
                          style={{ width: 100, margin: 0 }}
                          strokeColor={
                            v.completionPercent >= 80
                              ? "#10b981"
                              : v.completionPercent >= 50
                              ? "#f59e0b"
                              : "#ef4444"
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Empty description="No vehicle data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Hourly Distribution + Top Uploaders ────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14}>
          <SectionCard
            title="Hourly Distribution"
            subtitle="Upload activity by hour of day"
            extra={
              hourlyData && (
                <Tag
                  color="blue"
                  style={{
                    margin: 0,
                    borderRadius: 6,
                    fontSize: 11,
                  }}
                >
                  <FieldTimeOutlined style={{ marginRight: 4 }} />
                  Peak: {hourlyData.peakHour} ({hourlyData.peakHourCount}{" "}
                  uploads)
                </Tag>
              )
            }
          >
            {hourlyData && hourlyData.hourly?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={hourlyData.hourly}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    interval={2}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  />
                  <Bar
                    dataKey="total"
                    radius={[4, 4, 0, 0]}
                    name="Uploads"
                  >
                    {(hourlyData.hourly ?? []).map((item, i) => (
                      <Cell
                        key={i}
                        fill={
                          item.hour === hourlyData.peakHour
                            ? "#1677ff"
                            : "#cbd5e1"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No hourly data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={10}>
          <SectionCard
            title="Top Uploaders"
            subtitle="Most active members"
          >
            {uploaders && uploaders.results?.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {(uploaders.results ?? []).slice(0, 8).map((u, i) => (
                  <div
                    key={u.memberId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      background: i === 0 ? "#eff6ff" : "#f8fafc",
                      borderRadius: 10,
                      border: `1px solid ${
                        i === 0 ? "#bfdbfe" : "#f1f5f9"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background:
                          i === 0
                            ? "#1677ff"
                            : i === 1
                            ? "#10b981"
                            : i === 2
                            ? "#f59e0b"
                            : "#e2e8f0",
                        color: i < 3 ? "#fff" : "#64748b",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          display: "block",
                          lineHeight: 1.3,
                        }}
                        ellipsis
                      >
                        {u.name}
                      </Text>
                      <Text
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                        }}
                      >
                        {u.docTypesUsed} types ·{" "}
                        {u.vehiclesProcessed} vehicles
                      </Text>
                    </div>
                    <Text
                      strong
                      style={{ fontSize: 14, color: "#1677ff" }}
                    >
                      {u.totalUploads}
                    </Text>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No uploader data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Document Expiry + Entity Counts ────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14}>
          <SectionCard
            title="Document Expiry"
            subtitle="Compliance status overview"
            extra={
              docExpiry &&
              docExpiry.summary.expired > 0 && (
                <Tag
                  color="error"
                  style={{
                    margin: 0,
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  <WarningOutlined style={{ marginRight: 4 }} />
                  {docExpiry.summary.expired} expired
                </Tag>
              )
            }
          >
            {docExpiry ? (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  {expiryChartData.map((item) => (
                    <Col key={item.label} xs={12} sm={4}>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "10px 8px",
                          background: "#f8fafc",
                          borderRadius: 10,
                          borderLeft: `3px solid ${item.color}`,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: item.color,
                            display: "block",
                          }}
                        >
                          {item.value}
                        </Text>
                        <Text
                          style={{
                            fontSize: 11,
                            color: "#64748b",
                          }}
                        >
                          {item.label}
                        </Text>
                      </div>
                    </Col>
                  ))}
                </Row>
                {((docExpiry.expired?.length ?? 0) > 0 ||
                  (docExpiry.expiringSoon?.length ?? 0) > 0) && (
                  <Table
                    dataSource={[
                      ...(docExpiry.expired ?? []).map((d) => ({
                        ...d,
                        status: "expired",
                      })),
                      ...(docExpiry.expiringSoon ?? []).map((d) => ({
                        ...d,
                        status: "expiring",
                      })),
                    ].slice(0, 8)}
                    rowKey="documentId"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Type",
                        dataIndex: "documentType",
                        render: (v: string) => (
                          <Text style={{ fontSize: 12 }}>{v}</Text>
                        ),
                      },
                      {
                        title: "Company",
                        dataIndex: "companyName",
                        render: (v: string) => (
                          <Text
                            style={{ fontSize: 12 }}
                            ellipsis
                          >
                            {v}
                          </Text>
                        ),
                      },
                      {
                        title: "Expiry",
                        dataIndex: "expiryDate",
                        render: (v: string) => (
                          <Text style={{ fontSize: 12 }}>
                            {dayjs(v).format("DD MMM YYYY")}
                          </Text>
                        ),
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        render: (v: string) =>
                          v === "expired" ? (
                            <Tag
                              color="error"
                              style={{
                                borderRadius: 6,
                                fontSize: 11,
                                margin: 0,
                              }}
                            >
                              Expired
                            </Tag>
                          ) : (
                            <Tag
                              color="warning"
                              style={{
                                borderRadius: 6,
                                fontSize: 11,
                                margin: 0,
                              }}
                            >
                              Expiring
                            </Tag>
                          ),
                      },
                    ]}
                  />
                )}
              </>
            ) : (
              <Empty description="No expiry data" />
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={10}>
          <SectionCard
            title="Entity Counts"
            subtitle="Drivers, labours & members over time"
            extra={
              entityCounts && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    <TeamOutlined style={{ marginRight: 4 }} />
                    {entityCounts.totalDrivers} drivers
                  </Tag>
                  <Tag
                    style={{
                      margin: 0,
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                  >
                    {entityCounts.totalLabours} labours
                  </Tag>
                </div>
              )
            }
          >
            {entityCounts && entityCounts.trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={(entityCounts.trend ?? []).map((t) => ({
                    ...t,
                    date: dayjs(t.date).format("DD MMM"),
                  }))}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="drivers"
                    stroke="#1677ff"
                    strokeWidth={2}
                    dot={false}
                    name="Drivers"
                  />
                  <Line
                    type="monotone"
                    dataKey="labours"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                    name="Labours"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No entity trend data" />
            )}
          </SectionCard>
        </Col>
      </Row>

      {/* ─── Recent Activity ────────────────────────────── */}
      {(() => {
        const allItems = activity?.results ?? [];
        const uploads = allItems.filter((i) => i.type === "document_upload");
        const operations = allItems.filter((i) => i.type === "upload" || i.type === "rake_upload");

        const renderTimeline = (items: typeof allItems) => (
          <Timeline
            items={items.slice(0, 15).map((item) => ({
              color:
                item.module === "workflow"
                  ? "blue"
                  : item.module === "rake"
                  ? "purple"
                  : item.module === "weightment"
                  ? "green"
                  : "gray",
              children: (
                <div style={{ paddingBottom: 4 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: 6,
                        fontSize: 11,
                        textTransform: "capitalize",
                      }}
                    >
                      {item.module}
                    </Tag>
                    <Text style={{ fontSize: 13 }}>
                      {formatDocType(item.description)}
                    </Text>
                    {item.vehicleNumber && (
                      <Tag
                        color="default"
                        style={{
                          margin: 0,
                          borderRadius: 6,
                          fontSize: 11,
                        }}
                      >
                        <CarOutlined style={{ marginRight: 4 }} />
                        {item.vehicleNumber}
                      </Tag>
                    )}
                  </div>
                  <Text
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    <ClockCircleOutlined />
                    {dayjs(item.timestamp).fromNow()} · by{" "}
                    {item.uploadedBy}
                  </Text>
                </div>
              ),
            }))}
          />
        );

        return (
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <SectionCard
                title="Recent Uploads"
                subtitle="Latest document activity"
                extra={
                  uploads.length > 0 ? (
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                      color="blue"
                    >
                      {uploads.length} documents
                    </Tag>
                  ) : undefined
                }
              >
                {uploads.length > 0 ? (
                  renderTimeline(uploads)
                ) : (
                  <Empty description="No recent documents" />
                )}
              </SectionCard>
            </Col>
            <Col xs={24} xl={12}>
              <SectionCard
                title="Recent Operations"
                subtitle="Latest workflow & rake activity"
                extra={
                  operations.length > 0 ? (
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                      color="purple"
                    >
                      {operations.length} operations
                    </Tag>
                  ) : undefined
                }
              >
                {operations.length > 0 ? (
                  renderTimeline(operations)
                ) : (
                  <Empty description="No recent workflow or rake activity" />
                )}
              </SectionCard>
            </Col>
          </Row>
        );
      })()}
    </div>
  );
};

export default ControlPlaneDashboard;
